import { useLazyQuery, useQuery } from "@apollo/client";
import { Loader2, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gql } from "@/gql";
import { ME_QUERY } from "@/graphql/auth";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useAcceptInvitation, useRegisterWithInvitation, useValidateInvitation } from "../hooks";

const CHECK_EMAIL_EXISTS_QUERY = gql(`
  query CheckEmailExists($email: String!) {
    checkEmailExists(email: $email)
  }
`);

export const InvitationPage = () => {
  const { t } = useTranslation("auth");
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);

  const {
    invitation,
    loading: validating,
    error: validationError,
  } = useValidateInvitation(token || "");
  const { acceptInvitation, loading: accepting, error: acceptError } = useAcceptInvitation();
  const {
    registerWithInvitation,
    loading: registering,
    error: registerError,
  } = useRegisterWithInvitation();
  const [getMeQuery] = useLazyQuery(ME_QUERY);

  // Check if email exists
  const { data: emailCheckData, loading: checkingEmail } = useQuery(CHECK_EMAIL_EXISTS_QUERY, {
    variables: { email: invitation?.email || "" },
    skip: !invitation?.email,
  });

  const emailExists = emailCheckData?.checkEmailExists ?? false;

  // Redirect to login if email already has an account (only when user is not logged in)
  useEffect(() => {
    if (invitation?.email && !checkingEmail && emailExists && !user) {
      navigate(
        `/login?email=${encodeURIComponent(invitation.email)}&redirect=${encodeURIComponent(`/invite/${token}`)}`
      );
    }
  }, [invitation?.email, checkingEmail, emailExists, user, navigate, token]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: invitation?.email || "",
    password: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (invitation?.email) {
      setFormData((prev) => ({ ...prev, email: invitation.email }));
    }
  }, [invitation?.email]);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invitation.invalidTitle")}</CardTitle>
            <CardDescription>{t("invitation.invalidDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login")} className="w-full">
              {t("invitation.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (validating || checkingEmail) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (validationError || !invitation) {
    const isCancelled =
      validationError?.message?.includes("CANCELLED") ||
      validationError?.message?.includes("cancelled");
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invitation.invalidTitle")}</CardTitle>
            <CardDescription>
              {isCancelled
                ? t("invitation.cancelledDescription")
                : validationError?.message || t("invitation.invalidDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login")} className="w-full">
              {t("invitation.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.isExpired) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invitation.expiredTitle")}</CardTitle>
            <CardDescription>
              {t("invitation.expiredDescription", { salonName: invitation.salonName })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login")} className="w-full">
              {t("invitation.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.status === "ACCEPTED") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invitation.acceptedTitle")}</CardTitle>
            <CardDescription>
              {t("invitation.acceptedDescription", { salonName: invitation.salonName })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login")} className="w-full">
              {t("invitation.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAcceptInvitation = async () => {
    try {
      await acceptInvitation(token);
      // After accepting, redirect to dashboard
      navigate("/");
    } catch (error) {
      console.error("Failed to accept invitation:", error);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.reload(); // Reload to show registration form
  };

  const handleRegisterAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setFormError(t("invitation.allFieldsRequired"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError(t("errors.passwordMismatch"));
      return;
    }

    if (formData.password.length < 6) {
      setFormError(t("invitation.passwordMinLength"));
      return;
    }

    try {
      const result = await registerWithInvitation({
        token,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      if (result) {
        // Store tokens in Zustand store so Apollo client auth link can use them
        useAuthStore.setState({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          isAuthenticated: true,
        });

        // Now fetch user data - Apollo client will use the stored token from auth store
        const meResult = await getMeQuery();

        if (meResult.data?.me) {
          // Update with full user data
          login(
            {
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
            },
            meResult.data.me
          );
          // Navigate to home - AppContainer will handle franchise loading and salon selection
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Failed to register:", error);
    }
  };

  // If user is already logged in, check if it's the correct user
  if (user) {
    const isCorrectUser = user.email?.toLowerCase() === invitation.email.toLowerCase();

    // If wrong user, show warning and only logout option
    if (!isCorrectUser) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t("invitation.wrongUserTitle")}</CardTitle>
              <CardDescription>
                {t("invitation.wrongUserDescription", {
                  invitationEmail: invitation.email,
                  currentEmail: user.email,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleLogout} className="w-full">
                <LogOut className="mr-2 h-4 w-4" />
                {t("invitation.logoutAndContinue")}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Correct user - show accept button
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invitation.teamInvitationTitle")}</CardTitle>
            <CardDescription>
              {invitation.workerName
                ? t("invitation.teamInvitationDescriptionWithRole", {
                    salonName: invitation.salonName,
                    workerName: invitation.workerName,
                  })
                : t("invitation.teamInvitationDescription", { salonName: invitation.salonName })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {acceptError && (
              <Alert variant="destructive">
                <AlertDescription>{acceptError.message}</AlertDescription>
              </Alert>
            )}
            <Button onClick={handleAcceptInvitation} disabled={accepting} className="w-full">
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("invitation.accepting")}
                </>
              ) : (
                t("invitation.acceptInvitation")
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If email doesn't exist, show registration form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("invitation.joinTitle", { salonName: invitation.salonName })}</CardTitle>
          <CardDescription>
            {invitation.workerName
              ? t("invitation.joinDescriptionWithRole", { workerName: invitation.workerName })
              : t("invitation.joinDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegisterAndAccept} className="space-y-4">
            {(formError || registerError) && (
              <Alert variant="destructive">
                <AlertDescription>{formError || registerError?.message}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t("register.firstName")}</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t("register.lastName")}</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("register.email")}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("register.password")}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("register.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                minLength={6}
              />
            </div>

            <Button type="submit" disabled={registering} className="w-full">
              {registering ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("invitation.creatingAccount")}
                </>
              ) : (
                t("invitation.createAccountAndAccept")
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground mt-4">
              {t("register.hasAccount")}{" "}
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() =>
                  navigate(
                    `/login?email=${encodeURIComponent(formData.email)}&redirect=${encodeURIComponent(`/invite/${token}`)}`
                  )
                }
              >
                {t("register.signIn")}
              </Button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
